import { useState } from 'react'
import { Check, Copy } from 'lucide-react'
// Relative imports (not the '@/components' barrel) to avoid a cycle now that
// the barrel exports Workbench, which renders this file.
import { Button } from '../Button/Button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../Dialog/Dialog'

interface ShowStateDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  state: unknown
}

/**
 * Read-only popup showing the chart's JSON state. This is the exact payload a
 * future iteration will send to the AI as visual context — surfacing it now lets
 * us validate the shape before wiring persistence or AI integration.
 */
export default function ShowStateDialog({ open, onOpenChange, state }: ShowStateDialogProps) {
  const [copied, setCopied] = useState(false)
  const json = JSON.stringify(state, null, 2)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(json)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      /* clipboard unavailable — ignore */
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Workbench state</DialogTitle>
          <DialogDescription>
            JSON snapshot of the chart. A future iteration will send this to the AI as
            context (and persist it). Nothing is saved yet.
          </DialogDescription>
        </DialogHeader>
        <pre className="max-h-[55dvh] overflow-auto rounded-md bg-muted p-3 text-mono-sm text-foreground">
          {json}
        </pre>
        <DialogFooter>
          <Button variant="outline" size="sm" className="gap-1.5" onClick={handleCopy}>
            {copied ? <Check size={14} /> : <Copy size={14} />}
            {copied ? 'Copied' : 'Copy JSON'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
